import React from "react";
import { Link } from "react-router-dom";
import {
  Globe2,
  Layers,
  Network,
  Shield,
  Activity,
  Smartphone,
  Languages,
  Boxes,
  ArrowUpRight,
  ArrowRight,
} from "lucide-react";
import { PageHero, SectionWrapper, SectionHeader } from "../components/Section";

const FOUNDATIONS = [
  {
    icon: Globe2,
    title: "Web-Based Institutional Access",
    body: [
      "Allinstra solution environments are structured around browser-based access for standard institutional use cases, helping departments, teams, and authorized users work through a more accessible digital layer.",
      "This supports easier deployment, broader internal adoption, and more practical use across locations, departments, and user groups.",
    ],
  },
  {
    icon: Layers,
    title: "Modular Enterprise Structure",
    body: [
      "Allinstra’s portfolio is modular by design. Institutions can evaluate and combine product families according to operational priorities, implementation capacity, and phased transformation needs.",
      "The architecture supports separation between user interfaces, application logic, data structures, integrations, reporting, and operational workflows where appropriate.",
    ],
  },
  {
    icon: Network,
    title: "Service-Oriented Integration Logic",
    body: [
      "Allinstra is built with integration in mind. External systems, public data services, payment channels, identity and address systems, portals, and third-party environments can be evaluated through a service-oriented integration approach.",
      "Integration availability is always subject to local infrastructure, permissions, documentation quality, security rules, and third-party access.",
    ],
  },
  {
    icon: Shield,
    title: "Role-Based Control and Authorization",
    body: [
      "Institutional systems require clear boundaries. Allinstra supports role-based access structures that help define who can view, create, approve, edit, manage, or monitor information and workflows.",
      "This strengthens operational discipline and helps institutions protect process integrity.",
    ],
  },
  {
    icon: Activity,
    title: "Traceability, Logging and Accountability",
    body: [
      "Allinstra supports institutional traceability through workflow history, user activity records, document movement, transaction logs, and process visibility where configured.",
      "This helps institutions strengthen accountability, review process movement, and maintain clearer operational control.",
    ],
  },
  {
    icon: Smartphone,
    title: "Mobile, Portal and User Access Layers",
    body: [
      "Selected modules may support mobile use, field operations, internal portals, citizen-facing interfaces, industrialist or tenant portals, management views, and user-specific digital access channels.",
      "The final access model depends on selected modules, implementation design, user roles, local requirements, and technical feasibility.",
    ],
  },
  {
    icon: Languages,
    title: "Localization and Multilingual Readiness",
    body: [
      "Allinstra is designed for market adaptation. Localization may include language, terminology, forms, document structures, legal references, workflows, reporting needs, user roles, and country-specific process logic.",
      "Each market is evaluated separately before implementation.",
    ],
  },
  {
    icon: Boxes,
    title: "Flexible Deployment Planning",
    body: [
      "Deployment may be structured according to institutional needs, technical environment, data requirements, security expectations, and project scope.",
      "Cloud, hosted, on-premise, or hybrid deployment approaches may be evaluated where technically and commercially appropriate.",
    ],
  },
];

const Technology = () => (
  <main data-testid="page-technology">
    <PageHero
      overline="Technology"
      title="Technology built for institutional continuity."
      lead="Allinstra is designed for institutions that need dependable digital structure across workflows, documents, records, services, field operations, integrations, and management visibility."
    >
      <p className="max-w-3xl text-[15px] text-ink-600 leading-relaxed">
        The technology approach is modular, integration-aware, and built for long-term
        institutional use. It supports web-based access, role-based control, traceable process
        movement, service-oriented integration, localization, and flexible deployment planning.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/contact" data-testid="technology-hero-discuss" className="btn-primary">
          Discuss Technology <ArrowUpRight size={16} />
        </Link>
        <Link
          to="/product-portfolio"
          data-testid="technology-hero-portfolio"
          className="btn-secondary"
        >
          View Product Portfolio <ArrowRight size={14} />
        </Link>
      </div>
    </PageHero>

    <SectionWrapper id="technology-foundations">
      <SectionHeader
        overline="Technology foundations"
        title="The architecture behind connected institutional operations."
      />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {FOUNDATIONS.map((p, i) => (
          <article
            key={p.title}
            data-testid={`tech-foundation-${i + 1}`}
            className="bg-white border border-ink-200 p-7 sm:p-8 card-hover flex flex-col"
          >
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
                <p.icon size={18} strokeWidth={1.5} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-ink-500">
                {String(i + 1).padStart(2, "0")} / 08
              </div>
            </div>
            <h3 className="heading-display mt-5 text-xl font-semibold leading-snug">
              {p.title}
            </h3>
            <div className="mt-3 space-y-3 text-[14.5px] text-ink-600 leading-relaxed">
              {p.body.map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          </article>
        ))}
      </div>
    </SectionWrapper>

    <SectionWrapper id="technology-integration-aware" alt>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader
            overline="Integration-aware by design"
            title="Aligned with local infrastructure. Built for realistic deployment."
          />
        </div>
        <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-ink-700">
          <p>
            Institutional technology cannot assume that every market has the same level of
            public data access, e-government maturity, payment infrastructure, electronic
            signature availability, land registry access, GIS capability, or third-party API
            readiness.
          </p>
          <p className="text-ink-900">
            Allinstra’s technology approach is designed to respect those differences. Before
            implementation, each project is reviewed against available systems, integration
            permissions, data quality, institutional readiness, local legislation, and
            practical deployment conditions.
          </p>
        </div>
      </div>
    </SectionWrapper>

    <SectionWrapper id="technology-final-cta">
      <div className="border border-ink-200 p-8 sm:p-12 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="tag-overline">Final step</div>
            <h2 className="heading-display mt-3 text-2xl sm:text-3xl font-semibold">
              Build institutional technology with realistic integration planning.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
            <Link to="/contact" data-testid="technology-final-discuss" className="btn-primary">
              Discuss Technology <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/product-portfolio"
              data-testid="technology-final-portfolio"
              className="btn-secondary"
            >
              View Product Portfolio
            </Link>
          </div>
        </div>
      </div>
    </SectionWrapper>
  </main>
);

export default Technology;
